import type { WebshopConfig } from './webshopConfig';
import type { AssetPayload } from './assetPayload';
import { resolveToken, TokenResolutionError } from './resolveToken';

/**
 * V3-native Cloud asset I/O.
 *
 * Backs `inst.storeAsset()` (POST) and `Configento.loadAsset(id)` (GET)
 * against the admin's Laravel-hosted endpoints at:
 *
 *   POST {assetBaseUrl}/api/v1/storefront/assets        (auth: x-jscloud-tok)
 *   POST {assetBaseUrl}/api/v1/storefront/assets/images (auth: x-jscloud-tok) — combined image
 *   GET  {assetBaseUrl}/api/v1/storefront/assets/{id}   (no auth; UUID is the share token)
 *
 * Default `assetBaseUrl` is `admin.configento.app`; plugins can override
 * via the `data-configento-asset-base-url` mount-element attribute or the
 * embedded `_configentoPreview.assetBaseUrl`. Decoupled from
 * `cloudBaseUrl` because asset storage is admin's responsibility — not
 * the V3 cloud's (Java backend).
 *
 * Auth: reuses the same short-lived `x-jscloud-tok` the bundle already
 * uses for cloud component reads. On 401 the call refreshes the token
 * via `resolveToken({forceRefresh: true})` and retries once — same
 * pattern as `fetchComponent`. The merchant's secure_key never reaches
 * the browser.
 */

const DEFAULT_FILENAME = 'configuration';
const FALLBACK_ASSET_BASE = 'admin.configento.app';

/**
 * Discriminator for `ConfigentoCloudError.code`. Callers switch on this
 * instead of regex-on-message: that string contract is fragile, this is
 * machine-readable and stable.
 *
 *  - `NO_SELECTIONS`  — `storeAsset()` called before any Characteristic
 *                       is populated. User must configure first.
 *  - `CONFIG_MISSING` — the active instance has no `WebshopConfig`
 *                       (admin-iframe preview, unit-test contexts, or
 *                       `loadAsset` with no mounted configurator at all).
 *                       Setup bug; not a runtime retry.
 *  - `HTTP_4XX`       — Cloud refused with a 4xx response. Indicates a
 *                       client-side issue (auth, payload too big, bad
 *                       folder path). Do NOT retry.
 *  - `HTTP_5XX`       — Cloud returned 5xx. Transient — retry candidate.
 *  - `BAD_RESPONSE`   — Cloud returned 200 OK but the body wasn't a
 *                       valid asset envelope (`status: "error"`,
 *                       malformed JSON, non-object). Server is broken,
 *                       retry won't help.
 *  - `NETWORK`        — browser-level fetch failure (offline, CORS,
 *                       certificate, DNS). Transient — retry candidate.
 *  - `NOT_FOUND`      — `loadAsset(id)` got 404 from the asset endpoint.
 *                       Asset doesn't exist (expired, never stored, or
 *                       wrong customer/component context). Do NOT retry.
 */
export type ConfigentoCloudErrorCode =
    | 'NO_SELECTIONS'
    | 'CONFIG_MISSING'
    | 'HTTP_4XX'
    | 'HTTP_5XX'
    | 'BAD_RESPONSE'
    | 'NETWORK'
    | 'NOT_FOUND';

const RETRYABLE_CODES: ReadonlySet<ConfigentoCloudErrorCode> = new Set([
    'HTTP_5XX',
    'NETWORK',
]);

/**
 * Typed error thrown by `inst.storeAsset()` and `Configento.loadAsset()`.
 * The host's retry / user-message logic switches on `code`; `retryable`
 * is a derived convenience so callers don't re-encode the policy.
 *
 * Surfaced on the public API as `Configento.CloudError`, so host code
 * can `instanceof`-check via `err instanceof Configento.CloudError`.
 */
export class ConfigentoCloudError extends Error {
    readonly code:    ConfigentoCloudErrorCode;
    readonly status?: number;

    constructor(code: ConfigentoCloudErrorCode, message: string, status?: number) {
        super(message);
        this.name = 'ConfigentoCloudError';
        this.code = code;
        if (status !== undefined) this.status = status;
        Object.setPrototypeOf(this, ConfigentoCloudError.prototype);
    }

    /** Derived from `code` — `HTTP_5XX` and `NETWORK` are the only
     *  transient cases worth retrying. */
    get retryable(): boolean {
        return RETRYABLE_CODES.has(this.code);
    }
}

function classifyHttpStatus(status: number): 'HTTP_4XX' | 'HTTP_5XX' {
    return status >= 500 ? 'HTTP_5XX' : 'HTTP_4XX';
}

/** Shim WebshopConfig into the ConfiguratorPreviewContext shape that
 *  `resolveToken()` expects. Both types carry the same auth fields
 *  (token / tokenEndpoint / customer / cloudBaseUrl); the shim
 *  fills in the mandatory string defaults so TypeScript stays happy. */
function configToPreviewContext(config: WebshopConfig): {
    componentId: string;
    customer: string;
    token: string;
    tokenEndpoint?: string;
    cloudBaseUrl: string;
    taxRate: string;
    frontendVersion: string;
} {
    return {
        componentId:     config.componentId,
        customer:        config.customer ?? '',
        token:           config.token ?? '',
        tokenEndpoint:   config.tokenEndpoint,
        cloudBaseUrl:    config.cloudBaseUrl ?? '',
        taxRate:         (config.taxRate as string) ?? '',
        frontendVersion: config.frontendVersion ?? '',
    };
}

export interface StoredAsset {
    id:  string;
    url: string;
}

export interface StoreAssetOpts {
    /** Reserved for future use — the V3 endpoint stores one canonical
     *  payload per asset (the JSON header), so the filename concept
     *  from the legacy `storeasset.php` doesn't apply. Accepted for
     *  source compatibility with the type signature shipped in the
     *  first release of the API; currently ignored. */
    filename?: string;
}

export interface LoadAssetOpts {
    /** Reserved for future use. The V3 endpoint addresses assets by
     *  UUID alone — `filename` is not part of the URL. */
    filename?: string;
}

/**
 * POST the asset payload to the admin's `/api/v1/storefront/assets` and
 * return the public `{id, url}` for share-link composition.
 *
 * @throws ConfigentoCloudError
 */
export async function storeAssetPayload(
    config: WebshopConfig,
    payload: AssetPayload,
    _opts: StoreAssetOpts = {},
): Promise<StoredAsset> {
    return postAuthorized(config, storeEndpoint(config.assetBaseUrl), payload, 'storeAsset');
}

/**
 * POST the combined product image (a `data:image/…;base64,` URL as the
 * bundle renders it) to `/api/v1/storefront/assets/images` and return the
 * public `{id, url}` of the stored file. Hosts show it in their cart and
 * checkout — the URL is stable and publicly cacheable.
 *
 * @throws ConfigentoCloudError
 */
export async function storeCombinedImage(
    config: WebshopConfig,
    dataUrl: string,
): Promise<StoredAsset> {
    return postAuthorized(
        config,
        `${storeEndpoint(config.assetBaseUrl)}/images`,
        { image: dataUrl, component_id: config.componentId },
        'storeCombinedImage',
    );
}

/**
 * Token-authorised POST shared by the asset writes: resolve the token,
 * post, and on a 401 refresh it once and retry.
 */
async function postAuthorized(
    config: WebshopConfig,
    endpoint: string,
    body: unknown,
    label: string,
): Promise<StoredAsset> {
    // Eager token resolve — the storefront-plugin path emits
    // `config.token = ''` in the preview-context (the merchant's
    // secure_key never lands in cacheable HTML). Sending that empty
    // string is guaranteed to 401; instead we call the plugin's
    // tokenEndpoint upfront and use whatever it hands back. Same
    // pattern fetchComponent.ts uses. The plugin has its own 8-min
    // cache on the tokenEndpoint response, so this stays cheap on
    // hot paths.
    //
    // For admin-iframe preview (`token` populated, `tokenEndpoint`
    // absent) resolveToken() is a no-op that just returns config.token.
    let currentToken: string;
    try {
        currentToken = await resolveToken(configToPreviewContext(config));
    } catch (err) {
        if (err instanceof TokenResolutionError) {
            throw new ConfigentoCloudError(
                classifyHttpStatus(err.status),
                `${label} token resolve failed: HTTP ${err.status}`,
                err.status,
            );
        }
        throw err;
    }

    let response = await postJson(endpoint, body, currentToken, label);

    // 401 fallback: token expired between resolve and use, OR plugin
    // returned a stale cached token. Force-refresh via tokenEndpoint
    // (bypassing plugin cache with ?force=1) and retry once. Same
    // blast-radius as fetchComponent.ts.
    if (response.status === 401) {
        try {
            currentToken = await resolveToken(
                configToPreviewContext(config),
                { forceRefresh: true },
            );
            response = await postJson(endpoint, body, currentToken, label);
        } catch (err) {
            if (err instanceof TokenResolutionError) {
                throw new ConfigentoCloudError(
                    classifyHttpStatus(err.status),
                    `${label} token refresh failed: HTTP ${err.status}`,
                    err.status,
                );
            }
            throw err;
        }
    }

    if (!response.ok) {
        throw new ConfigentoCloudError(
            classifyHttpStatus(response.status),
            `${label} failed: HTTP ${response.status} at ${endpoint}`,
            response.status,
        );
    }
    const json = await safeJson(response, endpoint);
    if (typeof json.id !== 'string' || typeof json.url !== 'string') {
        throw new ConfigentoCloudError(
            'BAD_RESPONSE',
            `${label} returned unexpected shape: ${JSON.stringify(json).slice(0, 200)}`,
            response.status,
        );
    }
    return { id: json.id, url: json.url };
}

/**
 * GET the previously stored configuration payload by id. Unauthenticated —
 * the UUID is the share token.
 *
 * @throws ConfigentoCloudError
 */
export async function loadAssetPayload(
    config: WebshopConfig,
    id: string,
    _opts: LoadAssetOpts = {},
): Promise<AssetPayload> {
    // V3 assets are UUIDs. The legacy storefront made deep links with a
    // short plain hex id, stored per customer + component on the Java
    // cloud — those are read through the admin.
    const url = !LEGACY_DL_RE.test(id)
        ? loadUrl(config.assetBaseUrl, id)
        : `${baseUrl(config.assetBaseUrl)}/api/v1/storefront/legacy-deep-links/`
            + `${encodeURIComponent(config.customer ?? '')}/${encodeURIComponent(config.componentId)}/${encodeURIComponent(id)}`;

    let res: Response;
    try {
        res = await fetch(url, { method: 'GET' });
    } catch (err) {
        throw new ConfigentoCloudError(
            'NETWORK',
            `loadAsset network error: ${(err as Error).message}`,
        );
    }
    if (res.status === 404) {
        throw new ConfigentoCloudError(
            'NOT_FOUND',
            `Asset not found: ${id} (${url})`,
            404,
        );
    }
    if (!res.ok) {
        throw new ConfigentoCloudError(
            classifyHttpStatus(res.status),
            `loadAsset failed: HTTP ${res.status} at ${url}`,
            res.status,
        );
    }
    let json: unknown;
    try {
        json = await res.json();
    } catch (err) {
        throw new ConfigentoCloudError(
            'BAD_RESPONSE',
            `loadAsset returned non-JSON body at ${url}: ${(err as Error).message}`,
            res.status,
        );
    }
    if (json === null || typeof json !== 'object') {
        throw new ConfigentoCloudError(
            'BAD_RESPONSE',
            `loadAsset returned non-object at ${url}`,
            res.status,
        );
    }
    return json as AssetPayload;
}

async function postJson(
    endpoint: string,
    payload: unknown,
    token: string | undefined,
    label: string,
): Promise<Response> {
    try {
        return await fetch(endpoint, {
            method: 'POST',
            headers: {
                'content-type':  'application/json',
                'accept':        'application/json',
                'x-jscloud-tok': token ?? '',
            },
            body: JSON.stringify(payload),
        });
    } catch (err) {
        throw new ConfigentoCloudError(
            'NETWORK',
            `${label} network error: ${(err as Error).message}`,
        );
    }
}

/** Read a Response body as JSON, mapping decode errors to BAD_RESPONSE
 *  so callers don't get a raw SyntaxError. */
async function safeJson(res: Response, url: string): Promise<{ [k: string]: unknown }> {
    try {
        const v = (await res.json()) as unknown;
        if (v === null || typeof v !== 'object') {
            throw new ConfigentoCloudError(
                'BAD_RESPONSE',
                `response not a JSON object at ${url}`,
                res.status,
            );
        }
        return v as { [k: string]: unknown };
    } catch (err) {
        if (err instanceof ConfigentoCloudError) throw err;
        throw new ConfigentoCloudError(
            'BAD_RESPONSE',
            `response not JSON at ${url}: ${(err as Error).message}`,
            res.status,
        );
    }
}

function storeEndpoint(assetBaseUrl: string | undefined): string {
    return `${baseUrl(assetBaseUrl)}/api/v1/storefront/assets`;
}

const LEGACY_DL_RE = /^[0-9a-f]{6,40}$/i;

function loadUrl(assetBaseUrl: string | undefined, id: string): string {
    return `${baseUrl(assetBaseUrl)}/api/v1/storefront/assets/${encodeURIComponent(id)}`;
}

function baseUrl(assetBaseUrl: string | undefined): string {
    const base = (assetBaseUrl ?? FALLBACK_ASSET_BASE).replace(/\/+$/, '');
    const protocol = base.startsWith('http') ? '' : 'https://';
    return `${protocol}${base}`;
}

// Re-export the legacy DEFAULT_FILENAME constant for source compatibility
// with the previous API surface. Internal callers don't use it.
export { DEFAULT_FILENAME };
